/*
 * 포트빌 관리비 청구서 페이지 (PortvilleBillingPage)
 *
 * ⚠️  임시 접근 제한 적용됨 ⚠️
 *
 * 현재 이 페이지는 순차 오픈을 위해 특정 권한의 사용자만 접근할 수 있도록 제한되어 있습니다.
 *
 * 제한 조건:
 * - userLevel 4 이하: 접근 허용 (공사 관계자, 센터 관계자, 시스템 관리자)
 * - userLevel 5 이상: 접근 제한 → /site-preparation 페이지로 리다이렉트
 *
 * 정식 오픈 시 해제 방법:
 * 1. TEMPORARY_ACCESS_CONTROL 상수를 false로 변경
 * 2. 관련 주석 및 코드 블록 제거
 * 3. 사용자 인증 로직을 원래대로 복원
 *
 * 검색 키워드: "임시 접근 제한", "순차 오픈용", "TEMPORARY_ACCESS_CONTROL"
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../../../hooks/useAuth';
import { usePortvilleBillingView } from '../../../hooks/usePortvilleBillingView';
import { PortvilleBill } from '../../../types/portvilleBilling';
import ClientLayout from '../../../components/layout/ClientLayout';
import { db } from '../../../firebase';

// ===== 임시 접근 제한 설정 (순차 오픈용) =====
// TODO: 사이트 정식 오픈 시 이 플래그를 false로 변경하여 접근 제한을 해제하세요
const TEMPORARY_ACCESS_CONTROL = true;
// 허용되는 최대 userLevel (4 이하만 접근 가능, 5 이상은 제한)
const MAX_ALLOWED_USER_LEVEL = 4;
// ============================================

const PortvilleBillingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    bills,
    loading,
    error,
    loadBillsByUid,
    selectedYear,
    selectedMonth,
    setSelectedYear,
    setSelectedMonth
  } = usePortvilleBillingView();

  const [currentBill, setCurrentBill] = useState<PortvilleBill | null>(null);
  const [accessChecking, setAccessChecking] = useState(true);

  // ===== 임시 접근 제한 체크 (순차 오픈용) =====
  useEffect(() => {
    const checkUserAccess = async () => {
      if (!user) {
        navigate('/signin');
        return;
      }

      if (TEMPORARY_ACCESS_CONTROL && user) {
        try {
          const userProfileRef = doc(db, 'user_profiles', user.uid);
          const userProfileSnap = await getDoc(userProfileRef);

          if (userProfileSnap.exists()) {
            const userProfileData = userProfileSnap.data();
            const userLevel = userProfileData.userLevel || 999; // 기본값: 999 (최고 제한)

            // userLevel이 MAX_ALLOWED_USER_LEVEL보다 큰 경우 접근 제한
            if (userLevel > MAX_ALLOWED_USER_LEVEL) {
              console.log(`접근 제한: userLevel ${userLevel} > ${MAX_ALLOWED_USER_LEVEL}`);
              navigate('/site-preparation');
              return;
            }
          } else {
            // 사용자 프로필이 없는 경우도 접근 제한
            console.log('접근 제한: 사용자 프로필 없음');
            navigate('/site-preparation');
            return;
          }
        } catch (error) {
          console.error('사용자 권한 확인 실패:', error);
          navigate('/site-preparation');
          return;
        }
      }

      setAccessChecking(false);
    };

    checkUserAccess();
  }, [user, navigate]);
  // ============================================

  // 현재 연/월로 초기화
  useEffect(() => {
    const now = new Date();
    setSelectedYear(now.getFullYear());
    setSelectedMonth(now.getMonth() + 1);
  }, [setSelectedYear, setSelectedMonth]);

  // 사용자의 호실 정보가 있으면 청구서 로드
  useEffect(() => {
    if (user?.uid) {
      loadBillsByUid(user.uid, selectedYear, selectedMonth);
    }
  }, [user?.uid, loadBillsByUid, selectedYear, selectedMonth]);

  // 현재 월 청구서 찾기
  useEffect(() => {
    if (bills.length > 0) {
      const currentMonthBill = bills.find(bill => 
        bill.billingPeriod.year === selectedYear && 
        bill.billingPeriod.month === selectedMonth
      );
      setCurrentBill(currentMonthBill || null);
    } else {
      setCurrentBill(null);
    }
  }, [bills, selectedYear, selectedMonth]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ko-KR', {
      style: 'currency',
      currency: 'KRW'
    }).format(amount);
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return '미납';
      case 'paid': return '납부완료';
      case 'overdue': return '연체';
      case 'cancelled': return '취소';
      default: return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'paid': return 'text-green-600 bg-green-100';
      case 'overdue': return 'text-red-600 bg-red-100';
      case 'cancelled': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };


  if (loading || accessChecking) {
    return (
      <ClientLayout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">
              {accessChecking ? '접근 권한을 확인하는 중...' : '청구서를 불러오는 중...'}
            </p>
          </div>
        </div>
      </ClientLayout>
    );
  }

  if (error) {
    return (
      <ClientLayout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="text-red-600 text-6xl mb-4">⚠️</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">오류가 발생했습니다</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              다시 시도
            </button>
          </div>
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <div className="w-full min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 py-8">
        {/* 헤더 */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">포트빌 관리비 청구서</h1>
          <p className="text-gray-600">본인의 관리비 청구 내역을 확인하실 수 있습니다.</p>
        </div>

        {/* 연/월 선택 */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">조회 기간</h2>
          <div className="flex gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">연도</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {Array.from({ length: 3 }, (_, i) => {
                  const year = new Date().getFullYear() - i;
                  return (
                    <option key={year} value={year}>
                      {year}년
                    </option>
                  );
                })}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">월</label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1}월
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* 현재 월 청구서 */}
        {currentBill ? (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                {selectedYear}년 {selectedMonth}월 청구서
              </h2>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(currentBill.status)}`}>
                {getStatusText(currentBill.status)}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* 청구 정보 */}
              <div>
                <h3 className="text-md font-medium text-gray-900 mb-3">청구 정보</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">호실번호:</span>
                    <span className="font-medium">{currentBill.roomNumber}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      {currentBill.roomType === '시설물' ? '시설물명:' : '입주자명:'}
                    </span>
                    <span className="font-medium">
                      {currentBill.roomType === '시설물' && currentBill.facilityInfo 
                        ? currentBill.facilityInfo.facilityName 
                        : currentBill.residentInfo?.name || '정보 없음'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      {currentBill.roomType === '시설물' ? '담당자:' : '소속:'}
                    </span>
                    <span className="font-medium">
                      {currentBill.roomType === '시설물' && currentBill.facilityInfo 
                        ? currentBill.facilityInfo.responsiblePerson.name 
                        : currentBill.residentInfo?.organization || '정보 없음'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">청구기간:</span>
                    <span className="font-medium">
                      {currentBill.billingPeriod.year}년 {currentBill.billingPeriod.month}월
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">발행일:</span>
                    <span className="font-medium">
                      {new Date(currentBill.issuedDate).toLocaleDateString('ko-KR')}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">납부기한:</span>
                    <span className="font-medium">
                      {new Date(currentBill.dueDate).toLocaleDateString('ko-KR')}
                    </span>
                  </div>
                </div>
              </div>

              {/* 청구 금액 */}
              <div>
                <h3 className="text-md font-medium text-gray-900 mb-3">청구 금액</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">월 임대료:</span>
                    <span className="font-medium">{formatCurrency(currentBill.billItems.monthlyRent)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">관리비:</span>
                    <span className="font-medium">{formatCurrency(currentBill.billItems.managementFee)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">관리비 부가세:</span>
                    <span className="font-medium">{formatCurrency(currentBill.billItems.managementFeeVAT)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">전기요금:</span>
                    <span className="font-medium">{formatCurrency(currentBill.billItems.electricityFee)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">전기요금 부가세:</span>
                    <span className="font-medium">{formatCurrency(currentBill.billItems.electricityFeeVAT)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">전력기금:</span>
                    <span className="font-medium">{formatCurrency(currentBill.billItems.electricityFund)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">수도요금:</span>
                    <span className="font-medium">{formatCurrency(currentBill.billItems.waterFee)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">통신요금:</span>
                    <span className="font-medium">{formatCurrency(currentBill.billItems.communicationFee)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">통신요금 부가세:</span>
                    <span className="font-medium">{formatCurrency(currentBill.billItems.communicationFeeVAT)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">상계처리비:</span>
                    <span className="font-medium">{formatCurrency(currentBill.billItems.offsetProcessingFee)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">상계처리비 부가세:</span>
                    <span className="font-medium">{formatCurrency(currentBill.billItems.offsetProcessingFeeVAT)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">미납금:</span>
                    <span className="font-medium">{formatCurrency(currentBill.billItems.overdueAmount || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">연체 가산금:</span>
                    <span className="font-medium">{formatCurrency(currentBill.billItems.overduePenalty || 0)}</span>
                  </div>
                  <hr className="my-2" />
                  <div className="flex justify-between text-lg font-bold">
                    <span>총 청구금액:</span>
                    <span className="text-blue-600">{formatCurrency(currentBill.totalAmount)}</span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="text-center py-8">
              <div className="text-gray-400 text-6xl mb-4">📄</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {selectedYear}년 {selectedMonth}월 청구서가 없습니다
              </h3>
              <p className="text-gray-600">
                해당 기간의 청구서가 아직 발행되지 않았거나, 입주 기록이 없습니다.
              </p>
            </div>
          </div>
        )}

        {/* 청구서 목록 */}
        {bills.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">청구서 목록</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      청구기간
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      청구금액
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      상태
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      발행일
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      납부기한
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {bills.map((bill) => (
                    <tr key={bill.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {bill.billingPeriod.year}년 {bill.billingPeriod.month}월
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(bill.totalAmount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(bill.status)}`}>
                          {getStatusText(bill.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(bill.issuedDate).toLocaleDateString('ko-KR')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(bill.dueDate).toLocaleDateString('ko-KR')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        </div>
      </div>
    </ClientLayout>
  );
};

export default PortvilleBillingPage;
